import { externalEvents } from "@closerplatform/closer-sdk";
import { WsEventListener } from "@fidget/common/services/eventsListener";
import { IInboxStore, InboxFilterId } from "./InboxStore";
import { IInboxEntry } from "./InboxEntry";
import { InboxLoadingState } from "./InboxFilter";
import { ArtichokePresence } from "@closerplatform/spinner-openapi";
import { logger } from "@fidget/common/utils";

export class InboxEventListener implements WsEventListener {
  constructor(private _inboxFinder: () => IInboxStore, public name: string) {}

  get inboxStore(): IInboxStore {
    return this._inboxFinder();
  }

  guestProfileUpdated(event: externalEvents.GuestProfileUpdated) {
    this.inboxStore.findByRoomId(event.roomId, (entry: IInboxEntry) =>
      entry.owner.updateFromEvent(event)
    );
  }

  presenceUpdated(event: externalEvents.PresenceUpdated) {
    this.inboxStore.findByOwnerId(event.userId, (entry: IInboxEntry) => {
      entry.owner.setStatus(event.presence.valueOf() as ArtichokePresence);
    });
  }

  assigneeChanged(event: externalEvents.AssigneeChanged) {
    this.inboxStore.findByRoomId(event.roomId, (entry: IInboxEntry) =>
      entry.setAssignee(event.adviserId)
    );

    if (event.adviserId === this.inboxStore.currentUserProfile?.id) {
      this.inboxStore.reloadReassignEntryByRoomId(event.roomId);
    } else {
      this.inboxStore.reassignEntryByRoomId(event.roomId);
    }
  }

  assigneeRemoved(event: externalEvents.AssigneeRemoved) {
    this.inboxStore.findByRoomId(event.roomId, (entry: IInboxEntry) =>
      entry.setAssignee(undefined)
    );
    this.inboxStore.reassignEntryByRoomId(event.roomId);
  }

  followerAdded(event: externalEvents.FollowerAdded) {
    this.inboxStore.findByRoomId(event.roomId, (entry: IInboxEntry) =>
      entry.addFollower(event.adviserId)
    );

    if (event.adviserId === this.inboxStore.currentUserProfile?.id) {
      this.inboxStore.reloadReassignEntryByRoomId(event.roomId);
    } else {
      this.inboxStore.reassignEntryByRoomId(event.roomId);
    }
  }

  followerRemoved(event: externalEvents.FollowerRemoved) {
    this.inboxStore.findByRoomId(event.roomId, (entry: IInboxEntry) =>
      entry.removeFollower(event.adviserId)
    );

    this.inboxStore.reassignEntryByRoomId(event.roomId);
  }

  lastAdviserTimestampSet(event: externalEvents.LastAdviserTimestampSet) {
    this.inboxStore.findByRoomId(event.roomId, (entry: IInboxEntry) =>
      entry.setLastAdviserTimestamp(event.timestamp)
    );

    this.inboxStore.reassignEntryByRoomId(event.roomId);
  }

  lastAdviserTimestampRemoved(event: externalEvents.LastAdviserTimestampRemoved) {
    this.inboxStore.findByRoomId(event.roomId, (entry: IInboxEntry) =>
      entry.removeLastAdivserTimestamp()
    );

    this.inboxStore.reassignEntryByRoomId(event.roomId);
  }

  unreadCountUpdated(event: externalEvents.UnreadCountUpdated) {
    const currentRoom = this.inboxStore.currentRoomReference;
    if (currentRoom && currentRoom.id === event.roomId && currentRoom.isChatFocusedOnMostRecent) {
      this.inboxStore.findByRoomId(event.roomId, (entry) => entry.setUnreadCount(0));
      this.inboxStore.selectedFilter.recalculateUnreads();
    } else {
      this.inboxStore.findByRoomId(event.roomId, (entry) =>
        entry.setUnreadCount(event.unreadCount)
      );
      this.inboxStore.filters.forEach((f) => f.recalculateUnreads());
    }
  }

  unreadTotalUpdated(event: externalEvents.UnreadTotalUpdated) {
    if (this.inboxStore.globalFilter.isAnyFilterEnabled) {
      this.inboxStore.filters.forEach((f) => f.recalculateUnreads());
      return;
    }

    const { tab, unreadCount, unreadCountByTagGroup, unreadCountNoTagGroup } = event;
    const filter = this.inboxStore.filters.get(tab);
    if (!filter || filter.id === InboxFilterId.YOURS || filter.id === InboxFilterId.FOLLOWED) {
      return;
    }

    if (filter.id !== InboxFilterId.WAITING) {
      filter.setUnreadCount(unreadCount);

      return;
    }

    const unread = this.inboxStore.updateWaitingCounters(
      unreadCount,
      unreadCountNoTagGroup,
      unreadCountByTagGroup
    );

    const shouldRefresh =
      unread !== filter.loadedEntries.length &&
      filter.loadingState === InboxLoadingState.LOADED &&
      this.inboxStore.selectedFilter.id === filter.id;

    if (shouldRefresh) {
      filter.refresh();
    }
  }

  unassignedCountUpdated(event: externalEvents.UnassignedCountUpdated) {
    const { count, countByTagGroup, countNoTagGroup } = event;
    this.inboxStore.updateUnassignedCounters(
      count,
      countByTagGroup,
      countNoTagGroup
    );
  }

  conversationStatusChanged(event: externalEvents.ConversationStatusChanged) {
    logger.info(`Inbox conversationStatusChanged: ${event.roomId} status=${event.status}`);
    this.inboxStore.findByRoomId(event.roomId, (entry: IInboxEntry) => {
      logger.info(`Inbox conversationStatusChanged room found: ${event.roomId} status=${event.status}`);
      entry.setStatus(event.status);
    });
    this.inboxStore.reassignEntryByRoomId(event.roomId);
  }

  conversationSnoozed(event: externalEvents.ConversationSnoozed) {
    this.inboxStore.findByRoomId(event.roomId, (entry: IInboxEntry) => entry.snoozeConversation());
  }

  conversationUnsnoozed(event: externalEvents.ConversationUnsnoozed) {
    this.inboxStore.findByRoomId(event.roomId, (entry: IInboxEntry) =>
      entry.unsnoozeConversation()
    );
  }

  connected() {
    this.inboxStore.setup();
  }
}
